import { applyMask, MaskMode } from '@intigral/utils';
import { validateMobileNumber } from '@intigral/utils/lib/helpers/validators';
import {
    PARENTAL_LOCK_OTP_RESEND_COOLDOWN_SEC,
    PARENTAL_LOCK_OTP_RESEND_COOLDOWN_UNTIL_STORAGE_KEY,
    PARENTAL_LOCK_VERIFICATION_GUID_STORAGE_KEY,
    PARENTAL_LOCK_VERIFIED_OTP_STORAGE_KEY,
    PARENTAL_LOCK_VERIFIED_PARENTAL_PIN_STORAGE_KEY,
    parentalPinManagementConstants,
} from '../config/constants';
import { getConfiguration } from '../hooks/useConfiguration';

export const getParentalLockOtpResendCooldownSec = () => {
    const configured = Number(getConfiguration()?.kidsConfig?.otpResendCooldownSec);
    if (Number.isFinite(configured) && configured > 0) {
        return Math.floor(configured);
    }
    return PARENTAL_LOCK_OTP_RESEND_COOLDOWN_SEC;
};

// B2C / B2B2C: prefer mobile, otherwise email
export const getParentalLockOtpContact = (userInfo, username = '', phoneFallback = '') => {
    if (!userInfo) {
        return null;
    }

    const login = (username || userInfo.username || '').trim();
    const phone = (userInfo.msisdn || phoneFallback || '').trim();
    if (phone) {
        return { type: 'mobile', value: phone };
    }
    if (login && validateMobileNumber(login)) {
        return { type: 'mobile', value: login };
    }

    const email = (userInfo.email || '').trim();
    if (email) {
        return { type: 'email', value: email };
    }
    if (login.includes('@')) {
        return { type: 'email', value: login };
    }

    return null;
};

// check if user can reset parental pin with OTP instead of password
export const canUseParentalLockResetOtpFlow = (userInfo) => {
    if (!userInfo) {
        return false;
    }

    const segment = (userInfo.segment || '').toUpperCase();
    if (segment !== 'B2C' && segment !== 'B2B2C') {
        return false;
    }

    return Boolean(getParentalLockOtpContact(userInfo, userInfo.username));
};

// same routing as PasswordPopup forgot-password when parental-lock OTP is not used
export const getParentalLockOtpFallbackScreen = (userInfo, msisdn = '') => {
    const segment = (userInfo?.segment || '').toUpperCase();
    const phone = (msisdn || userInfo?.msisdn || '').trim();
    if (segment === 'B2C' && phone.length > 0) {
        return parentalPinManagementConstants.ACCOUNT_RETRIEVAL_OTP_GENERATE;
    }
    return parentalPinManagementConstants.CHANGE;
};

// mask email or phone shown on OTP screen
export const getMaskedOtpContact = (contact) => applyMask(contact?.value || '', MaskMode.Partial);

export const getParentalLockOtpToastErrorCode = (error) => {
    const payload = error?.data && (error.data.subCode || error.data.code) ? error.data : error;
    return payload?.subCode || payload?.code;
};

export const isCannotGenerateParentalLockOtpError = (error) => {
    if (!error) {
        return false;
    }
    const code = getParentalLockOtpToastErrorCode(error);
    return code === 'CANNOT_GENERATE_OTP' || Number(code) === 42007;
};

export const persistParentalLockOtpResendCooldown = () => {
    const until = Date.now() + getParentalLockOtpResendCooldownSec() * 1000;
    sessionStorage.setItem(PARENTAL_LOCK_OTP_RESEND_COOLDOWN_UNTIL_STORAGE_KEY, String(until));
};

export const getParentalLockOtpResendCooldownRemainingSec = () => {
    const raw = sessionStorage.getItem(PARENTAL_LOCK_OTP_RESEND_COOLDOWN_UNTIL_STORAGE_KEY);
    if (!raw) {
        return 0;
    }

    const remaining = Math.ceil((Number(raw) - Date.now()) / 1000);
    if (remaining <= 0) {
        sessionStorage.removeItem(PARENTAL_LOCK_OTP_RESEND_COOLDOWN_UNTIL_STORAGE_KEY);
        return 0;
    }

    return remaining;
};

export const clearParentalLockOtpResendCooldown = () => {
    sessionStorage.removeItem(PARENTAL_LOCK_OTP_RESEND_COOLDOWN_UNTIL_STORAGE_KEY);
};

export const readVerificationGuid = () => {
    return sessionStorage.getItem(PARENTAL_LOCK_VERIFICATION_GUID_STORAGE_KEY);
};

export const storeVerificationGuid = (guid) => {
    sessionStorage.setItem(PARENTAL_LOCK_VERIFICATION_GUID_STORAGE_KEY, guid);
};

export const clearVerificationGuidStorage = () => {
    sessionStorage.removeItem(PARENTAL_LOCK_VERIFICATION_GUID_STORAGE_KEY);
};

export const readVerifiedOtp = () => {
    return sessionStorage.getItem(PARENTAL_LOCK_VERIFIED_OTP_STORAGE_KEY);
};

export const storeVerifiedOtp = (otp) => {
    sessionStorage.setItem(PARENTAL_LOCK_VERIFIED_OTP_STORAGE_KEY, otp);
};

export const clearVerifiedOtpStorage = () => {
    sessionStorage.removeItem(PARENTAL_LOCK_VERIFIED_OTP_STORAGE_KEY);
};

export const readVerifiedParentalPin = () => {
    return sessionStorage.getItem(PARENTAL_LOCK_VERIFIED_PARENTAL_PIN_STORAGE_KEY);
};

export const storeVerifiedParentalPin = (pin) => {
    sessionStorage.setItem(PARENTAL_LOCK_VERIFIED_PARENTAL_PIN_STORAGE_KEY, pin);
};

export const clearVerifiedParentalPinStorage = () => {
    sessionStorage.removeItem(PARENTAL_LOCK_VERIFIED_PARENTAL_PIN_STORAGE_KEY);
};
